/** Accounting's browser call host. Existing queue/worker remains the actor. */
import { VoiceIntake } from './voiceIntake';
import { RealtimeInput } from './realtimeInput';
import { RealtimeNarration } from './realtimeNarration';

export class LiveVoiceHost {
  constructor({ owner, currentOwner, page, api, storage, getContext = () => ({}), getAttachments = () => [],
    onThread = () => {}, onSnapshot = () => {}, onAccepted = () => {}, onState = () => {},
    transportFactory = (options) => new RealtimeInput(options), readerFactory = (options) => new RealtimeNarration(options),
    now = () => performance.now(), random = window.crypto, documentRef = document, wakeLock = navigator.wakeLock }) {
    Object.assign(this, { owner, currentOwner, api, getContext, getAttachments, onThread, onSnapshot, onAccepted,
      onState, transportFactory, readerFactory, now, random, documentRef, wakeLock });
    this.intake = new VoiceIntake({ owner, currentOwner, page, api, storage, random });
    this.run = null;
  }

  current(run) { return this.run === run && !run.disposed && this.currentOwner() === this.owner; }
  intended(intent) { return Boolean(intent && this.intent === intent && !intent.cancelled
    && this.currentOwner() === this.owner && this.documentRef.visibilityState === 'visible'); }
  observe(action) { try { Promise.resolve(action()).catch(() => {}); } catch (_) { /* Optional surface update. */ } }

  publish(run, patch = {}) {
    if (!this.current(run)) return;
    Object.assign(run.view, patch);
    let saved = null;
    try { saved = this.intake.read(); } catch (_) { /* Retain raw storage; show recovery. */ }
    const unsaved = (run.view.pendingInputs || []).filter((item) => item.transcript
      && !saved?.inputs.some((row) => row.itemId === item.itemId)
      && !saved?.captureDrafts?.some((row) => row.itemId === item.itemId));
    this.observe(() => this.onState({ ...run.view, startedAt: run.intent?.startedAt,
      renewing: Boolean(run.renewing), repeatSpeech: Boolean(run.intent?.repeatSpeech),
      connectionRecovered: Boolean(run.intent?.connectionRecovered),
      detail: run.view.detail || run.intent?.awakeProblem || null,
      microphoneMuted: run.renewing || run.view.phase === 'connecting' ? Boolean(run.intent?.muted) : run.view.phase === 'muted', journal: saved ? {
      inputs: saved.inputs, captureDrafts: saved.captureDrafts || [], session: saved.session, endRequested: saved.endRequested,
    } : null,
      captureActive: !run.mediaEnded && Boolean(run.transport?.run) && !run.transport.run.ended,
      pendingInputs: unsaved,
      unconfirmedInputs: (saved?.inputs.filter((item) => !item.receipt).length || 0) + (saved?.captureDrafts?.length || 0) + unsaved.length }));
  }

  async acquireWake(intent) {
    if (!this.wakeLock?.request) { intent.awakeProblem = 'screen_awake_unavailable'; return; }
    try {
      const lease = await this.wakeLock.request('screen');
      if (!this.intended(intent)) { this.observe(() => lease.release()); return; }
      intent.wake = lease;
      this.observe(() => lease.addEventListener?.('release', () => {
        if (this.intended(intent)) {
          intent.awakeProblem = 'screen_awake_released';
          if (this.run?.intent === intent) this.publish(this.run);
        }
      }));
    } catch (_) {
      if (this.intended(intent)) {
        intent.awakeProblem = 'screen_awake_unavailable';
        if (this.run?.intent === intent) this.publish(this.run);
      }
    }
  }

  cancelIntent(intent) {
    if (!intent || intent.cancelled) return;
    intent.cancelled = true;
    this.observe(() => this.documentRef.removeEventListener('visibilitychange', intent.visibility));
    if (intent.wake) this.observe(() => intent.wake.release());
    intent.wake = null;
    if (this.intent === intent) this.intent = null;
  }

  quiet(run) {
    try {
      const saved = this.intake.read();
      return !run.mediaEnded && !run.speakingItem && !run.view.thinking && !run.intent?.attachmentPause
        && !run.narrationPending && !run.reader?.inflight
        && !['speaking','audio_starting'].includes(run.view.audioPhase)
        && !run.view.queuedInputs?.length && !run.view.pendingInputs?.some((item) => item.transcript)
        && saved && saved.inputs.every((item) => item.receipt) && !saved.captureDrafts?.length
        && run.transport.quiescent?.() !== false && this.now()-run.lastActivityAt >= 2000;
    } catch (_) { return false; }
  }

  async renew(run, unexpected = false) {
    const intent = run.intent;
    if (!this.current(run) || !this.intended(intent) || run.renewing || !run.session
        || (!unexpected && !this.quiet(run))) return false;
    if (unexpected && intent.errors >= 2) { await this.end(run); this.publish(run, { detail: 'voice_reconnect_stopped' }); return false; }
    if (unexpected) { intent.errors += 1; intent.connectionRecovered = true; }
    run.renewing = true;
    this.publish(run);
    try {
      if (!run.mediaEnded) {
        // Pause acquisition and allow already-sent speech to surface. If a late
        // original/answer appears, defer planned renewal rather than discard it.
        run.renewalDraining = true; run.transport.mute(true);
        if (!unexpected) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          if (!this.current(run) || !this.intended(intent)) return false;
          if (!this.quiet(run)) {
            run.renewing = false; run.renewalDraining = false;
            run.transport.mute(Boolean(intent.attachmentPause) || intent.muted); this.publish(run); return false;
          }
        }
      }
      await this.end(run, true);
      if (!this.current(run) || !this.intended(intent)) return false;
      if (this.intake.read() || run.view.pendingInputs?.some((item) => item.transcript)) {
        this.cancelIntent(intent); run.renewing = false; this.publish(run, { recovery: true }); return false;
      }
      // Accepted financial work keeps running once. Wait for its durable answer,
      // with End/background/owner-change remaining able to cancel this wait.
      while (this.current(run) && this.intended(intent)
          && (run.view.thinking || run.view.queuedInputs?.length || intent.attachmentPause)) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      if (!this.current(run) || !this.intended(intent)) return false;
      const [call, thread] = await Promise.all([
        this.api.inspect(run.session.session_id, run.capability), this.api.thread(run.session.thread_id),
      ]);
      if (!this.current(run) || !this.intended(intent)) return false;
      if (call.data.status !== 'ended' || thread.data.active_turn
          || call.data.inputs.some((item) => ['waiting','needs_attention'].includes(item.status))) {
        this.cancelIntent(intent); run.renewing = false; this.publish(run, { recovery: true }); return false;
      }
      const original = [...call.data.inputs].sort((a,b) => b.owner_input_revision-a.owner_input_revision)[0];
      const answer = (thread.data.messages || []).find((message) => message.turn_id === original?.turn_id
        && message.role === 'assistant' && message.agent_status === 'completed' && !run.seen.has(message.turn_id));
      const continuation = { session_id: run.session.session_id, device_token: run.capability,
        carry_turn_id: answer?.turn_id || null };
      run.disposed = true; clearInterval(run.ownerTimer); clearTimeout(run.pollTimer); run.pollResolve?.();
      return await this.start(run.session.thread_id, continuation, intent);
    } catch (_) {
      this.cancelIntent(intent); run.renewing = false;
      if (this.current(run)) this.publish(run, { phase: 'ended', recovery: Boolean(this.intake.read()) });
      return false; // Unknown setup is reconciled by its frozen identity, never blindly recreated.
    }
  }

  async start(threadId = null, continuation = null, continuingIntent = null) {
    if (this.recoveryPromise) throw new Error('Voice recovery is still running.');
    if (this.run?.view.pendingInputs?.some((item) => item.transcript)) throw new Error('Copy or discard the unsent speech before restarting.');
    if (this.run && !this.run.mediaEnded) throw new Error('End the current voice call first.');
    if (this.intake.read()) throw new Error('Reconcile the saved voice call before restarting.');
    if (continuingIntent && !this.intended(continuingIntent)) return false;
    const intent = continuingIntent || { muted: false, errors: 0, cancelled: false, startedAt: Date.now() };
    this.intent = intent;
    if (!continuingIntent) {
      intent.visibility = () => { if (this.documentRef.visibilityState !== 'visible') this.observe(() => this.end()); };
      try { this.documentRef.addEventListener('visibilitychange', intent.visibility); }
      catch (_) { this.cancelIntent(intent); throw new Error('Voice lifecycle observation is unavailable.'); }
      this.observe(() => this.acquireWake(intent));
    }
    const run = { disposed: false, mediaEnded: false, startedAt: this.now(), session: null, reader: null, spokenReplies: this.spokenReplies !== false,
      intent, renewing: Boolean(continuingIntent), lastActivityAt: this.now(),
      seen: new Set(), playbacks: new Map(), suppression: 0, failures: 0, view: { phase: 'connecting', audioPhase: 'idle' } };
    this.run = run;
    const transport = this.transportFactory({ output: true, initialMuted: intent.muted || Boolean(intent.attachmentPause), wakeLock: null, readyTimeoutMs: 30000,
      createSession: async ({ sdp }) => {
        if (!this.current(run) || run.mediaEnded) throw new Error('Voice setup ended.');
        const pending = this.intake.createSession({ sdp, threadId, continuation });
        // createSession journals synchronously before its first network await.
        // Freeze cleanup identity too: an old callback must never end a new call.
        const saved = this.intake.read();
        run.request = saved?.request; run.capability = saved?.capability;
        const session = await pending;
        run.session = session;
        if (!this.current(run) || run.mediaEnded) { await this.endServer(run); throw new Error('Voice setup ended.'); }
        if (this.onThread(session.thread_id) === false) { await this.endServer(run); throw new Error('Conversation continuity could not be saved.'); }
        if (!this.current(run) || run.mediaEnded) { await this.endServer(run); throw new Error('Voice setup ended.'); }
        run.reader = this.readerFactory({ sessionId: session.session_id, transport,
          onState: (state) => { run.lastActivityAt = this.now(); this.publish(run, { audioPhase: state.phase, audioDetail: state.detail }); },
          report: (playbackId, kind, extra) => {
            if (!this.current(run)) return;
            return this.api.report(session.session_id, { playback_id: playbackId, event_id: this.random.randomUUID(),
              kind, elapsed_ms: Math.floor(this.now()-run.startedAt), ...extra }, run.capability);
          } });
        return session;
      },
      endSession: () => this.endServer(run),
      onInput: async (input) => {
        if (!this.current(run) || run.mediaEnded) throw new Error('That voice generation ended.');
        const receipt = await this.intake.admit(input, { context: this.getContext(),
          attachmentIds: this.getAttachments().map((item) => item.attachment_id) });
        if (this.current(run)) {
          run.lastActivityAt = this.now();
          run.latestAdmittedRevision = receipt.owner_input_revision;
          intent.repeatSpeech = false;
          intent.connectionRecovered = false;
          if (run.speakingItem === input.itemId) run.speakingItem = null;
          const frozen = this.intake.read()?.inputs.find((item) => item.itemId === input.itemId);
          if (frozen) this.observe(() => this.onAccepted(receipt, frozen.payload.attachment_ids));
          this.publish(run);
        }
        return receipt;
      },
      onSpeechStart: ({ itemId }) => {
        if (!this.current(run) || run.mediaEnded) return;
        run.lastActivityAt = this.now(); run.speakingItem = itemId; run.suppression += 1; run.reader?.interrupt();
        intent.repeatSpeech = false;
        if (run.view.detail === 'speech_not_transcribed') this.publish(run, { detail: null });
      },
      onTranscriptionIssue: (issue) => {
        if (!this.current(run)) return;
        intent.repeatSpeech = true;
        this.observe(() => this.api.transcriptionIssue?.(run.session.session_id, issue, run.capability));
        if (issue.provider_error_code === 'token_expired') this.observe(() => this.renew(run, true));
      },
      onOutputEvent: (event, id) => run.reader?.event(event, id) || false,
      onAudioState: (event) => run.reader?.audioState(event),
      onState: (state) => {
        if (!this.current(run)) return;
        run.lastActivityAt = this.now();
        if (state.detail === 'speech_not_transcribed') run.speakingItem = null;
        if (state.phase === 'ended') {
          run.mediaEnded = true; clearInterval(run.heartbeat); clearInterval(run.renewTimer);
          if (!run.renewing) clearInterval(run.ownerTimer);
        }
        this.publish(run, state);
        if (state.phase === 'ended') {
          run.mediaEnded = true; run.reader?.close();
          try {
            const retained = this.intake.retainUnsubmittedSpeech(state.pendingInputs);
            if (!retained && state.pendingInputs?.some((item) => item.transcript)) throw new Error('No local journal');
            run.view.pendingInputs = []; this.publish(run);
          }
          catch (_) { this.publish(run, { recovery: true, pendingInputs: state.pendingInputs }); }
          this.observe(() => this.endServer(run).then(() => this.current(run) ? this.reconcile() : null)
            .catch(() => this.publish(run, { recovery: true })));
          if (!run.renewing) {
            if (['connection_lost','unexpected_provider_response'].includes(state.detail)) this.observe(() => this.renew(run, true));
            else if (this.intent === intent) this.cancelIntent(intent);
          }
        }
      },
    });
    run.transport = transport;
    run.ownerTimer = setInterval(() => {
      if (this.currentOwner() !== this.owner) this.dispose();
    }, 1000);
    const ready = await transport.start();
    if (!this.current(run)) return false;
    if (!ready) { run.mediaEnded = true; clearInterval(run.ownerTimer); this.cancelIntent(intent);
      run.renewing = false; this.publish(run, { phase: 'ended', recovery: Boolean(this.intake.read()) }); return false; }
    if (!this.intended(intent)) { await this.end(run); return false; }
    run.renewing = false;
    this.publish(run);
    run.heartbeat = setInterval(() => {
      if (this.current(run) && !run.mediaEnded && !run.heartbeatInFlight) this.observe(async () => {
        run.heartbeatInFlight = true;
        try {
          const receipt = await this.intake.heartbeat(); run.heartbeatFailures = 0;
          if (this.current(run) && receipt.status !== 'active') await this.renew(run, true);
        } catch (error) {
          if (this.current(run)) {
            run.heartbeatFailures = (run.heartbeatFailures || 0)+1;
            if ([401,403,404].includes(error.response?.status)) await this.end(run);
            else if (run.heartbeatFailures >= 3) await this.renew(run, true);
            else this.publish(run, { connectionLost: true });
          }
        } finally { run.heartbeatInFlight = false; }
      });
    }, 30000);
    if (Number.isFinite(run.session.renew_after_ms) && run.session.renew_after_ms > 0) {
      run.renewTimer = setInterval(() => {
        if (this.current(run) && this.intended(intent) && !run.mediaEnded && !run.renewing
            && this.now()-run.startedAt >= run.session.renew_after_ms && this.quiet(run)) this.observe(() => this.renew(run));
      }, 1000);
    }
    this.observePolling(run);
    return true;
  }

  endServer(run) {
    if (this.currentOwner() !== this.owner) return Promise.reject(new Error('Voice owner changed.'));
    if (!run.request && !run.session) return Promise.resolve(null);
    if (!run.endPromise) {
      const saved = this.intake.read();
      run.endPromise = (saved?.request.request_key === run.request?.request_key ? this.intake.end()
        : run.session ? this.api.end(run.session.session_id, run.capability)
          : this.api.endRequest(run.request, run.capability))
        .catch((error) => { run.endPromise = null; throw error; });
    }
    return run.endPromise;
  }

  async end(run = this.run, keepIntent = false) {
    if (run && this.run !== run) return;
    if (!keepIntent) { this.cancelIntent(this.intent); if (run) run.renewing = false; }
    if (!run) return this.reconcile();
    run.mediaEnded = true; clearInterval(run.heartbeat); clearInterval(run.renewTimer); run.reader?.close();
    if (!keepIntent) clearInterval(run.ownerTimer);
    await run.transport?.end();
    try { await this.endServer(run); if (this.current(run)) await this.reconcile(); }
    catch (_) { this.publish(run, { phase: 'ended', recovery: true }); }
  }

  mute(value) {
    if (this.intent) this.intent.muted = Boolean(value);
    if (!this.intent?.attachmentPause && !this.run?.renewalDraining) this.run?.transport.mute(value);
    if (this.run) this.publish(this.run);
  }
  stopSpeaking() { if (this.run) { this.run.suppression += 1; this.run.reader?.interrupt(); } }
  discardUnsavedSpeech(itemId) {
    const run = this.run;
    if (!run || !run.mediaEnded || !this.current(run)) throw new Error('End capture before discarding speech.');
    run.view.pendingInputs = (run.view.pendingInputs || []).filter((item) => item.itemId !== itemId);
    this.publish(run);
  }
  setSpokenReplies(value) {
    this.spokenReplies = Boolean(value);
    if (this.run) { this.run.spokenReplies = Boolean(value); if (!value) this.stopSpeaking(); }
  }

  pauseForAttachment() {
    const run = this.run; const intent = run?.intent;
    if (!run || !this.current(run) || !this.intended(intent) || (run.mediaEnded && !run.renewing)) return () => {};
    const token = {};
    intent.attachmentPause = token;
    if (!run.mediaEnded) run.transport.mute(true);
    this.stopSpeaking();
    return () => {
      if (this.intended(intent) && intent.attachmentPause === token) {
        intent.attachmentPause = null;
        const active = this.run;
        if (active && this.current(active) && active.intent === intent && !active.mediaEnded) {
          active.lastActivityAt = this.now();
          if (!active.renewalDraining) active.transport.mute(intent.muted);
        }
      }
    };
  }

  observePolling(run) {
    if (!run.pollPromise) run.pollPromise = this.poll(run).finally(() => { run.pollPromise = null; });
    this.observe(() => run.pollPromise);
  }

  reconcile() {
    if (!this.recoveryPromise) {
      this.recoveryPromise = this.reconcileSaved().finally(() => { this.recoveryPromise = null; });
    }
    return this.recoveryPromise;
  }

  async reconcileSaved() {
    if (this.run && !this.run.mediaEnded) throw new Error('End the current voice call before recovery.');
    let journal = this.intake.read();
    if (!journal) return null;
    if (!journal.endRequested || !['ended','failed'].includes(journal.session?.status)) await this.intake.end();
    journal = await this.intake.reconcileInputs();
    if (journal.session?.thread_id && this.onThread(journal.session.thread_id) === false) {
      throw new Error('Conversation continuity could not be saved. The voice request remains available for recovery.');
    }
    for (const item of journal.inputs) {
      if (item.receipt) this.observe(() => this.onAccepted(item.receipt, item.payload.attachment_ids));
    }
    if (journal.session?.thread_id) {
      const response = await this.api.thread(journal.session.thread_id);
      this.intake.requireOwner();
      this.observe(() => this.onSnapshot(response.data));
      if (this.run) this.publish(this.run, { thinking: Boolean(response.data.active_turn) });
    }
    if (!this.run && journal.session?.session_id) {
      this.run = { mediaEnded: true, disposed: false, session: journal.session, capability: journal.capability,
        request: journal.request, failures: 0, view: { phase: 'ended' }, seen: new Set() };
    }
    if (journal.inputs.every((item) => item.receipt) && !journal.captureDrafts?.length) {
      try { this.intake.clearReconciled(); } catch (_) { /* Keep remote closure uncertainty saved. */ }
    }
    if (this.run) {
      this.publish(this.run, { phase: 'ended', recovery: Boolean(this.intake.read()) });
      this.observePolling(this.run);
    }
    return journal;
  }

  async poll(run) {
    while (this.current(run)) {
      try {
        const journal = this.intake.read();
        if (!run.session) return;
        if (!journal && !run.mediaEnded) { await this.end(run); return; }
        const [threadReply, callReply] = await Promise.all([
          this.api.thread(run.session.thread_id), this.api.inspect(run.session.session_id, journal?.capability || run.capability),
        ]);
        if (!this.current(run)) return;
        run.capability = journal?.capability || run.capability;
        run.failures = 0;
        const thread = threadReply.data; const inputs = callReply.data.inputs;
        const carry = run.session.continuation;
        this.observe(() => this.onSnapshot(thread));
        const unresolved = inputs.filter((item) => ['waiting','needs_attention'].includes(item.status));
        this.publish(run, { queuedInputs: unresolved, thinking: Boolean(thread.active_turn), connectionLost: false });
        if (!run.mediaEnded && callReply.data.status !== 'active') await this.end(run);
        if (!run.mediaEnded && !run.speakingItem) {
          const latest = Math.max(run.latestAdmittedRevision || 0, carry?.owner_input_revision || 0, ...inputs.map((item) => item.owner_input_revision));
          const eligible = (thread.messages || []).filter((message) => message.role === 'assistant' && message.agent_status === 'completed'
            && (inputs.some((item) => item.turn_id === message.turn_id && item.owner_input_revision === latest)
              || (carry?.turn_id === message.turn_id && carry.owner_input_revision === latest))
            && !run.seen.has(message.turn_id));
          if (eligible.length) {
            if (!run.spokenReplies) { eligible.forEach((answer) => run.seen.add(answer.turn_id)); }
            else {
              eligible.slice(0,-1).forEach((message) => run.seen.add(message.turn_id));
              const answer = eligible[eligible.length-1];
              const suppression = run.suppression;
              const playbackId = run.playbacks.get(answer.turn_id) || this.random.randomUUID(); run.playbacks.set(answer.turn_id, playbackId);
              try {
                run.narrationPending = true;
                const prepared = await this.api.narration(run.session.session_id, { turn_id: answer.turn_id, playback_id: playbackId }, journal.capability);
                if (this.current(run) && !run.mediaEnded && !run.speakingItem && suppression === run.suppression) {
                  run.reader.request(prepared.data); run.seen.add(answer.turn_id);
                } else if (!run.mediaEnded || suppression !== run.suppression) run.seen.add(answer.turn_id);
              } catch (error) {
                if (error.response?.data?.code !== 'voice_answer_not_ready') run.seen.add(answer.turn_id);
                this.publish(run, { audioPhase: 'audio_unavailable' });
              } finally { run.narrationPending = false; }
            }
          }
        }
        if (run.mediaEnded && !thread.active_turn && !unresolved.length) return;
      } catch (_) {
        if (!this.current(run)) return;
        run.failures += 1; this.publish(run, { connectionLost: true });
        if (run.failures >= 3) { await this.end(run); return; }
      }
      await new Promise((resolve) => { run.pollTimer = setTimeout(resolve, 1000); run.pollResolve = resolve; });
    }
  }

  dispose() {
    const run = this.run;
    if (!run) return;
    this.observe(() => this.end());
    run.disposed = true; clearInterval(run.heartbeat); clearInterval(run.ownerTimer); clearInterval(run.renewTimer); clearTimeout(run.pollTimer); run.pollResolve?.();
  }
}
